import {
  createExtensionInput,
  coreExtensionData,
} from '@backstage/frontend-plugin-api';
import {
  EntityContentBlueprint,
  EntityCardBlueprint,
} from '@backstage/plugin-catalog-react/alpha';
import { useEntity } from '@backstage/plugin-catalog-react';
import { EntityContentLayout } from './EntityContentLayout';

const CARD_ORDER = [
  'entity-card:catalog-graph/relations',
  'entity-card:catalog/depends-on-resources',
  'entity-card:catalog/depends-on-components',
  'entity-card:catalog/has-subcomponents',
];

const GROUP_CARD_ORDER = [
  'entity-card:org/group-profile',
  'entity-card:catalog/about',
  'entity-card:catalog/links',
  'entity-card:org/ownership',
  'entity-card:org/members-list',
  'entity-card:catalog-graph/relations',
];

const USER_CARD_ORDER = [
  'entity-card:org/ownership',
  'entity-card:catalog-graph/relations',
];

const cardOrder = (id: string, order = CARD_ORDER) => {
  const idx = order.indexOf(id);
  return idx === -1 ? Infinity : idx;
};

export const catalogOverviewOverride = EntityContentBlueprint.makeWithOverrides(
  {
    name: 'overview',
    inputs: {
      cards: createExtensionInput([
        coreExtensionData.reactElement,
        EntityContentBlueprint.dataRefs.filterFunction.optional(),
        EntityCardBlueprint.dataRefs.type.optional(),
      ]),
    },
    factory(originalFactory, { inputs }) {
      const sortedCards = [...inputs.cards].sort(
        (a, b) => cardOrder(a.node.spec.id) - cardOrder(b.node.spec.id),
      );

      return originalFactory({
        path: '/',
        title: 'Overview',
        group: 'overview',
        loader: async () => {
          const cards = sortedCards.map(card => ({
            id: card.node.spec.id,
            element: card.get(coreExtensionData.reactElement),
            type: card.get(EntityCardBlueprint.dataRefs.type),
            filter:
              card.get(EntityContentBlueprint.dataRefs.filterFunction) ??
              (() => true),
          }));

          const Component = () => {
            const { entity } = useEntity();
            const visibleCards = cards.filter(c => c.filter(entity));
            const kind = entity.kind.toLowerCase();
            let entityCardOrder: string[] | undefined;
            if (kind === 'group') {
              entityCardOrder = GROUP_CARD_ORDER;
            } else if (kind === 'user') {
              entityCardOrder = USER_CARD_ORDER;
            }

            if (entityCardOrder) {
              visibleCards.sort(
                (a, b) =>
                  cardOrder(a.id, entityCardOrder) -
                  cardOrder(b.id, entityCardOrder),
              );
            }

            return <EntityContentLayout cards={visibleCards} />;
          };

          return <Component />;
        },
      });
    },
  },
);
